import React, { useMemo } from 'react'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import pluralize from 'pluralize'
import { Layout, Sidebar, TwoColumns } from 'app/theme/layout'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import BookTruckBanner, { BookTruckBannerV3 } from 'app/theme/banners/BookTruckBar'
import ShadowBox from 'app/caterers/ShadowBox'
import Pagination from 'app/theme/Pagination'
import HtmlContent from 'app/theme/HtmlContent'
import NotFoundPage from 'app/pages/NotFoundPage'
import { format } from 'date-fns'
import { useSearchParams } from 'app/theme/useSearchParams'
import { useLocation, Redirect } from 'react-router-dom'
import getCrumbs from './getCrumbs'
import TagSelection from './TagSelection'
import TopCuisines from './TopCuisines'
import { expandPageContent } from '../utils/expandPageContent'

import balloons from 'assets/svg/balloons.svg'
import rings from 'assets/svg/rings.svg'
import mic from 'assets/svg/mic.svg'
import computer from 'assets/svg/computer.svg'
import partyHat from 'assets/svg/party-hat.svg'
import burger from 'assets/svg/burger.svg'
import megaphone from 'assets/svg/megaphone.svg'

const monthYear = format(new Date(), 'MMMM yyyy')

const asPossessive = (term) => {
  return term?.endsWith("s") ? `${term}'` : `${term}'s`
}

export default function CityListPage({ city, page, tagSlug }) {
  const [searchParams] = useSearchParams()
  const searchTerm = searchParams.search
  const path = `/food-trucks/${city.url_slug}`
  const location = useLocation()

  const areaFilter = {}
  if ((city.type === 'city' && city.major) || !city.location) {
    areaFilter.city_id = city.id
  } else {
    // Include both: show caterers who have this city as related OR whose service address is within radius
    const radius = searchParams.service_radius || 50
    areaFilter.city_id_or_service_area = JSON.stringify({
      city_id: city.id,
      service_area: { ...city.location, radius }
    })
  }

  const query = useQuery(['caterers', {
    sort: '-ranking',
    filter: {
      ...areaFilter,
      tag_slug: tagSlug || undefined,
      closed: false,
      search: searchTerm,
    },
    include: [
      'photo',
    ],
    fields: {
      caterers: ['name', 'slug'],
      photos: ['uuid', 'extension', 'path']
    },
    page: {
      number: page || 1,
      size: 21
    },
    stats: {
      total: 'count',
    }
  }])

  const tagQuery = useQuery(tagSlug && ['tags', {
    filter: {
      slug: tagSlug,
      visible: JSON.stringify({
        city_cuisine_list: {
          visible: true,
        }
      })
    },
    fields: {
      tags: ['name', 'slug', 'primary', 'city_description', 'region_description', 'display_rules']
    }
  }])

  const tagsQuery = useQuery(['tags', {
    sort: '-total_caterers',
    filter: {
      ...areaFilter,
      primary: true,
      visible: JSON.stringify({
        city_filters: {
          visible: true,
        }
      })
    },
    fields: {
      tags: ['name', 'slug', 'display_rules'],
    },
    extra_fields: {
      tags: ['total_caterers'],
    },
    page: {
      number: 1,
      size: 5,
    },
    stats: {
      total: 'count',
    }
  }])

  const pageQuery = useQuery(['pages', {
    sort: '-priority',
    merge: true,
    expand_content: true,
    filter: {
      type: tagSlug ? 'CityTagList' : ['CityMap', 'CityList'],
      city: city.url_slug,
      tag: tagSlug ? tagSlug : undefined,
    },
  }])

  let caterers = query.data ? [...query.data] : []
  let tag = tagQuery.data ? tagQuery.data[0] : null
  let pageDetails = pageQuery.data ? pageQuery.data[0] : null

  const tagNameSingular = tag ? pluralize.singular(tag?.name) : null
  const crumbs = getCrumbs(city, tag, searchTerm)

  const cityDisplayName = [
    city.name,
    city.country?.code === 'US' ? city.region?.code : (city.region?.name || city.country?.name)
  ].filter(Boolean).join(', ')

  const googleMetaTags = useMemo(() => {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'itemListOrder': 'ItemListUnordered',
      'numberOfItems': caterers.length,
      'itemListElement': caterers.map((caterer, index) => ({
        '@type': 'Restaurant',
        name: caterer.name,
        position: index,
        '@id': `https://roaminghunger.com/${caterer.slug}/#Restaurant`,
        url: `https://roaminghunger.com/${caterer.slug}/`
      })),
      'description': `Looking for the best tacos in ${cityDisplayName}? From traditional Mexican dishes to brand new Asian fusion, we have 47 ${cityDisplayName} taco trucks, trailers, and carts for you to explore. Find one near you for lunch or book them for your next party. Whether you're craving street tacos, fish tacos, birria, or burritos and quesadillas, you'll find something you love.`,
      'additionalType': 'https://www.wikidata.org/wiki/Q3303463'
    })
  }, [caterers])

  if (query.isLoading || tagQuery.isLoading || pageQuery.isLoading || tagsQuery.isLoading) {
    return null
  }

  if (!searchTerm && !query.data?.length) {
    return <NotFoundPage />
  }

  if (pageDetails?.content) {
    const currentDate = new Date()
    pageDetails.content = expandPageContent(pageDetails.content, {
      COUNT: query.meta?.stats?.total?.count,
      CITY_NAME: city.name,
      REGION_NAME: city.region?.name,
      REGION_ABBREV: city.region?.code,
      TAG_NAME: tag?.name,
      TAG_SLUG: tag?.slug,
      CURRENT_YEAR: currentDate.getFullYear(),
      CURRENT_MONTH: currentDate.toLocaleString('default', { month: 'long' }),
    })
  }

  const count = query.meta?.stats?.total?.count
  const pageOutOfRange = page && Math.ceil(count / 21) < page && parseInt(page) !== 1
  const metaTitleCount = !searchTerm && (!page && count >= 5) ? (count > 21 ? "The 21 Best" : `The ${count} Best`) : ''

  if ((tagSlug && !tag)) {
    return <NotFoundPage />
  }

  if (count && page && pageOutOfRange) {
    // If I try to go to page 8 and there is only 1 page's worth of search results
    // We used to show a 404 page, but now
    // we will redirect to the first page of results
    return <Redirect to={`/food-trucks/${city.url_slug}/1/`} />
  }

  if (caterers.length) {
    caterers.splice(3, 0, 'banner')
  }

  let metaTitle
  let metaDescription

  if (tag) {
    metaTitle = `${metaTitleCount} ${cityDisplayName} ${tagNameSingular} Food Trucks (${monthYear}) - Roaming Hunger`
    metaDescription = `Find the best ${tag.name} Food Trucks in ${cityDisplayName} and book or rent a ${tag.name} food truck, trailer, cart, or pop-up for your next catering, party or event.`
  } else {
    const tagsStatement = tagsQuery.data.length > 1 ? `—from ${tagsQuery.data[0].name} to ${tagsQuery.data[1].name}— ` : ''
    metaTitle = `Guide to ${cityDisplayName} Food Trucks (${monthYear}) - Roaming Hunger`
    metaDescription = `Explore ${city.name ? asPossessive(city.name) + ' ' : ''}${count} food trucks and the most popular cuisines ${tagsStatement}for office lunches, parties, and events.`
  }

  let metaKeywords = [
    city.name,
    city.region?.name,
    city.country && city.country.code !== 'US' && city.country.name,
    tag && tag.name,
    'catering,events,food trucks,food carts,food trucks near me,street food,truck schedules'
  ].filter(Boolean).join(',')

  if (pageDetails?.content.meta_title) {
    metaTitle = pageDetails.content.meta_title
  }

  if (pageDetails?.content.meta_description) {
    metaDescription = pageDetails.content.meta_description
  } else {
  }

  if (pageDetails?.content.meta_keywords) {
    metaKeywords = pageDetails.content.meta_keywords
  }

  const parseTagCityDescription = () => {
    let base = tag.city_description
    base = base.replaceAll("{{CITY_NAME}}", city.name)
    base = base.replaceAll("{{REGION_NAME}}", city.region?.name)
    base = base.replaceAll("{{REGION_ABBREV}}", city.region?.code)
    base = base.replaceAll("{{COUNT}}", query.meta.stats.total.count)
    return base
  }

  let bannerUrl = `/${city.url_slug}/food-truck-catering/`

  // Don't index pages for small cities with less than 3 trucks or neighborhoods
  const skipIndexing = (city.type === 'city' && !city.major && !city.market && (!count || count < 3)) || city.type === 'neighborhood'

  return (
    <Layout className="container py-10">
      <TwoColumns sidebar={<Sidebar city={city} tag={tag} />}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription}></meta>
          <meta name="keywords" content={metaKeywords}></meta>
          <meta name="og:title" content={metaTitle}></meta>
          <script type="application/ld+json">
            {googleMetaTags}
          </script>
          {page && (
            <link rel="canonical" href={`/food-trucks/${city.url_slug}/`} />
          )}
          {skipIndexing ? (
            <meta name="robots" content="noindex" />
          ) : null }
        </Helmet>
        <Breadcrumbs
          crumbs={crumbs}
        />
        <div className="mt-4 flex items-center">
          <h1 className="text-3xl lg:text-4xl text-navy font-platform leading-tight">
            {pageDetails?.content.heading || (
              tag ? `Top ${city.name} ${tagNameSingular} Trucks` : `Top ${city.name} Food Trucks`
            )}
          </h1>
          {/*
          {subcities?.length > 0 && (
            <CityDropdown
              cities={subcities}
              className="ml-3 mt-2"
            />
          )}
          */}
        </div>

          <div>
            <HtmlContent
              className="mt-3"
              content={pageDetails?.content.description || (
                tag?.city_description ? parseTagCityDescription() : city.short_description
              )}
            />
            <div
              className={[
                'justify-between items-center mt-4 pt-4 pb-3',
                'border-t border-gray-300',
                'sm:flex sm:pb-1 md:mb-2'
              ]}
            >
              {caterers.length ? (
                <TagSelection
                  place={city}
                  baseUrl={`/food-trucks/${city.url_slug}`}
                  active={tagSlug}
                />
              ) : null}
            </div>
            {pageDetails?.content.subheading && (
              <h2 className="text-navy leading-none text-xl mb-6 md:text-2xl">
                {pageDetails?.content.subheading}
              </h2>
            )}
            {caterers.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-6">
                {caterers.map((caterer, index) => (
                  caterer === 'banner' ? (
                    <div key="banner" className="hidden md:flex md:col-span-3">
                      <BookTruckBannerV3 city={city} url={bannerUrl} />
                    </div>
                  ) : (
                    <ShadowBox
                      key={caterer.id}
                      caterer={caterer}
                      photo={caterer.photo}
                    />
                  )
                ))}
              </div>
            ) : (
              <div className="text-lg italic font-light">
                {searchTerm ? (
                  <p>
                    Sorry, we couldn't find any trucks or carts matching "{searchTerm}".
                  </p>
                ) : (
                  <p>
                    Sorry, we couldn't find any Food Trucks or Food Carts in {city.name}.
                  </p>
                )}
              </div>
            )}

            {query.meta && (
              <Pagination
                className="justify-center mt-10"
                total={query.meta.stats.total.count}
                current={page || 1}
                limit={21}
                getLinkProps={link => ({
                  href: tag
                    ? `${path}/${tag.slug}/${link.page === 1 ? '' : `${link.page}/`}${location.search || ""}`
                    : `${path}/${link.page}/${location.search || ""}`
                })}
                extraLinks={({ hasPreviousPage, hasNextPage }) => (
                  <Helmet>
                    {hasPreviousPage && (
                      <link
                        rel="prev"
                        href={ tag
                          ? `${path}/${tag.slug}/${page - 1 === 1 ? '' : `${page}/`}`
                          : `${path}/${(page || 1) - 1}/`
                        }
                      />
                    )}
                    {hasNextPage && (
                      <link rel="next" href={tag ? `${path}/${tag.slug}/${parseInt(page || 1) + 1}/` : `${path}/${parseInt(page || 1) + 1}/`} />
                    )}
                  </Helmet>
                )}
              />
            )}
          </div>
        {page === 1 && !caterers.length ? (
          <p>No Caterers Found</p>
        ) : null}

        {!tagSlug && (page === 1 || !page) && (
          <>
            <TopCuisines
              className="mt-10"
              city={city}
              areaFilter={areaFilter}
            />
            <BringTruckToEvent city={city} />
            <FoodTruckRentals city={city} />
            {city.description && (
              <HtmlContent
                className="mt-10 pt-10 border-t border-gray-300"
                content={city.description}
              />
            )}
            <FrequentlyAskedQuestions 
              city={city}
              caterers={caterers}
              tagsQuery={tagsQuery}
              query={query}
            />
          </>
        )}
      </TwoColumns>
    </Layout>
  )
}

function BringTruckToEvent({city}) {
  return (
    <div className="my-10">
      <h2 className="text-navy text-4xl font-platform">
        Bring a Food Truck to Any {city.name} Event
      </h2>
      <p>
        From food service at a corporate park to an intimate backyard celebration,
        find and book the perfect food truck, cart, or pop-up.
      </p>
      <div className="mt-2">
        <div className="flex mt-5">
          <div className="flex justify-center">
            <img src={computer} height="100px" width="100px" style={{width: "50px"}}/>
          </div>
          <div className="flex-1 pl-4">
            <h3 className="text-black">Corporate Catering</h3>
            <p className="text-md font-light py-1">Meal Programs, Employee or Client Appreciation, Company Celebrations</p>
            <a className="text-blue" href={`/food-truck-catering/corporate-catering/`}>Explore Corporate Catering</a>
          </div>
        </div>

        <div className="flex mt-5">
          <div className="flex justify-center">
            <img src={partyHat} height="100px" width="100px" style={{width: "50px"}}/>
          </div>
          <div className="flex-1 pl-4">
            <h3 className="text-black">Private Parties</h3>
            <p className="text-md font-light py-1">Graduations, Birthdays, Anniversaries, Baby Showers, and more</p>
            <a className="text-blue" href={`/food-truck-catering/party-catering/`}>Explore Private Parties</a>
          </div>
        </div>

        <div className="flex mt-5">
          <div className="flex justify-center">
            <img src={rings} height="100px" width="100px" style={{width: "50px"}}/>
          </div>
          <div className="flex-1 pl-4">
            <h3 className="text-black">Large-Scale Events</h3>
            <p className="text-md font-light py-1">Conferences, Festivals, Sporting Events, Employee Events</p>
            <a className="text-blue" href={`/food-truck-catering/large-event-food-concessions/`}>Explore Large-Scale Events</a>
          </div>
        </div>

        <div className="flex mt-5">
          <div className="flex justify-center">
            <img src={mic} height="100px" width="100px" style={{width: "50px"}}/>
          </div>
          <div className="flex-1 pl-4">
            <h3 className="text-black">Food Trucks for Weddings</h3>
            <p className="text-md font-light py-1">Wedding Receptions, Rehearsal Dinners, Late Night Snacks</p>
            <a className="text-blue" href={`/food-truck-catering/food-truck-wedding/`}>Explore Wedding Catering</a>
          </div>
        </div>

        <div className="flex mt-5">
          <div className="flex justify-center">
            <img src={balloons} height="100px" width="100px" style={{width: "50px"}}/>
          </div>
          <div className="flex-1 pl-4">
            <h3 className="text-black">Community Events</h3>
            <p className="text-md font-light py-1">School Events, Religious Gatherings, Street Fairs, Neighborhood Parties</p>
            <a className="text-blue" href={`/food-truck-catering/local-community-concessions/`}>Explore Community Events</a>
          </div>
        </div>
      </div>
    </div>
  )
}

function FoodTruckRentals({city}) {
  return (
    <div className="my-10">
      <h2 className="text-navy text-4xl font-platform">
        Food Truck Rentals in {city.name}
      </h2>
      <p className="mb-5 mt-2">
        Looking To Rent A Food Truck? You're in the right place. Just select what type of food truck rental you're looking for.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="shadow-lg text-center">
          <div className="bg-blue text-center p-4">
            <div className="flex justify-center" style={{height: "50px"}}>
              <img src={burger} className="inline" style={{width: "50px"}}/>
            </div>
            <p className="text-xs text-white">Catering</p>
            <p className="text-white">Bring a Food Truck to Your Next Catered Event</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-sm font-light mb-4">
              Looking for food truck catering for a private or corporate event?
            </p>
            <a
              className="btn btn-green px-4 py-1 font-platform font-light"
              children="Book a Food Truck"
              href={`/${city.url_slug}/food-truck-catering/`}
            />
          </div>
        </div>
        <div className="shadow-lg text-center">
          <div className="bg-navy text-center p-4">
            <div className="flex justify-center" style={{height: "50px"}}>
              <img src={megaphone} className="inline" style={{width: "50px"}}/>
            </div>
            <p className="text-xs text-white">Activation</p>
            <p className="text-white">Launch Branded Food Trucks for Marketing Promotions</p>
          </div>
          <div className="bg-white p-4">
            <p className="text-sm font-light mb-4">
              Looking to create an experiential marketing activation?
            </p>
            <a
              className="btn btn-green px-4 py-1 font-platform font-light"
              children="Visit our Agency"
              href="/experiential-marketing-agency/"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function FrequentlyAskedQuestions({city, caterers, tagsQuery, query}) {
  return (
    <div className="my-10">
      <h2 className="text-navy text-4xl font-platform">
        Frequently Asked Questions
      </h2>
      <div className="bg-white border mt-3 p-5">
        <h3 className="text-lg mb-2">What are the best food trucks in {city.name}?</h3>
        <p className="font-light">
          {city.name} has some amazing food trucks. Choosing the “best” depends on what you're looking for (a great lunch truck or a great food truck to cater your wedding). You can explore the top food trucks by going to our {city.name} List page. 
        </p>
        <p className="font-light my-2">
          If you're looking for food trucks that are great for catering, here are the top 5 most booked food trucks on Roaming Hunger:
        </p>
        <ol className="font-light list-decimal list-inside">
          {caterers.filter(c => c !== 'banner').slice(0,5).map((caterer) => (
            <li key={`caterer-${caterer.id}`}>
              <a href={`/${caterer.slug}/`} className="text-blue" target="_blank">{caterer.name}</a>
            </li>
          ))}
        </ol>
      </div>
      {tagsQuery.data && (
        <div className="bg-white border mt-3 p-5">
          <h3 className="text-lg mb-2">What are the most popular food truck cuisines in {city.name}?</h3>
          <p className="font-light mb-2">
            Based on the food trucks in our system, here are the cuisines that are most popular with food trucks in {city.name}:
          </p>
          <ol className="font-light list-decimal list-inside">
            {tagsQuery.data.slice(0,5).map((tag) => (
              <li key={`tag-${tag.id}`}>
                <a href={`/food-trucks/${city.url_slug}/${tag.slug}/`} className="text-blue">{tag.name}</a>
              </li>
            ))}
          </ol>
        </div>
      )}
      <div className="bg-white border mt-3 p-5">
        <h3 className="text-lg mb-2">How many food trucks are in {city.name}?</h3>
        <p className="font-light">
          There are currently {query.meta.stats.total.count} food trucks, trailers, carts, and pop-ups on Roaming Hunger in the {city.name} area. Keep in mind this is not a complete list because food trucks manually join Roaming Hunger. Our system may also include food trucks that are no longer operating because we rely on the food trucks themselves to let us know when they are closed.
        </p>
      </div>
      <div className="bg-white border mt-3 p-5">
        <h3 className="text-lg mb-2">Are food trucks popular in {city.name}?</h3>
        <p className="font-light">
          Based on the fact that there are {query.meta.stats.total.count} food trucks, trailers, and carts in the area, it's clear that the mobile food culture is thriving in the {city.name} area.
        </p>
      </div>
      <div className="bg-white border mt-3 p-5">
        <h3 className="text-lg mb-2">Where can I find food trucks in {city.name}?</h3>
        <p className="font-light">
          You can use the Roaming Hunger map above to see food trucks in the area.
        </p>
      </div>
      <div className="bg-white border mt-3 p-5">
        <h3 className="text-lg mb-2">How can I rent a food truck in {city.name}?</h3>
        <p className="font-light">
          If you're looking for catering, simply go to our <a href={`/${city.url_slug}/food-truck-catering/`} className="text-blue">{city.name} food truck catering page</a> and fill out a request form.
        </p>
        <p className="font-light">
          If you're a brand or marketing agency looking to rent a food truck for an activation or promotion, go to our <a href={`/experiential-marketing-agency/`} className="text-blue">agency page</a>.
        </p>
      </div>
      <div className="bg-white border mt-3 p-5">
        <h3 className="text-lg mb-2">How do I book a food truck for my event?</h3>
        <p className="font-light">
          Use Roaming Hunger to book a food truck for any event in {city.name}. Simply go to our <a href={`/${city.url_slug}/food-truck-catering/`} className="text-blue">{city.name} food truck catering page</a> to get started.
        </p>
        <p className="font-light my-2">
          After you fill out your event information, local food trucks will apply based on your event date and budget with a catering package. From there you'll be able to choose the best package for your event.
        </p>
        <p className="font-light">
          If it's a public event where the food truck is selling to the public, you'll fill out your event information and local food trucks will apply to learn more about your event. You can choose which food trucks to connect with from there.
        </p>
      </div>
    </div>
  )
}